import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
    ReactiveFormsModule,
    FormsModule
} from "@angular/forms";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { Subject, takeUntil } from "rxjs";
import { animate, style, transition, trigger } from "@angular/animations";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NavigationMenuSelection } from "../../../interfaces/navigation_menu.interface";
import {
    CreateQuestionResponseCommentPayload,
    QuestionResponseCommentDto,
    QuestionResponseCommentsService
} from "../../../services/question_response_comments.service";
import {
    QuestionResponseMutationResponse,
    QuestionResponsesService
} from "../../../services/question_responses.service";
import { ProductLicenseSummary, ProductLicensesService } from "../../../services/product_licenses.service";
import { LoginService } from "../../../services/login.service";
import { Subscription } from "rxjs";
import { FormNavigationDispatcherService } from "../../../services/form_navigation_dispatcher.service";
import { formatDateTimeDisplay } from "../../../utils/date_time_display.util";

interface AssessorNote {
    id: number;
    userId: number | null;
    body: string;
    userRole: string | null;
    createdAt: string;
    updatedAt: string | null;
    questionReference: string | null;
    createdByDisplayName: string | null;
}

const SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
const TARGET_PATTERN = /^[A-Za-z0-9 _.-]+$/;

@Component({
    selector: "td[appSaqAssessorNotesFooter]",
    templateUrl: "./assessor_notes.component.html",
    styleUrls: ["./assessor_notes.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzIconModule,
        NzModalModule,
        NzSpinModule,
        NzSelectModule,
        NzPaginationModule
    ],
    animations: [
        trigger("rowAnimation", [
            transition(":enter", [
                style({ opacity: 0, transform: "translateY(-6px)" }),
                animate("200ms ease-out", style({ opacity: 1, transform: "translateY(0)" }))
            ]),
            transition(":leave", [
                style({ height: "*", opacity: 1, transform: "translateY(0)" }),
                animate(
                    "180ms ease-in",
                    style({
                        height: 0,
                        opacity: 0,
                        transform: "translateY(-8px)",
                        marginTop: 0,
                        marginBottom: 0,
                        paddingTop: 0,
                        paddingBottom: 0
                    })
                )
            ])
        ])
    ]
})
export class AssessorNotesFooterComponent implements OnChanges, OnDestroy {
    private static nextInstanceId = 0;

    @Input()
    sectionId = "";

    @Input()
    sectionKey = "";

    @Input()
    readOnlyUser = false;

    @Input()
    aggregateByLicense = false;

    @Input()
    productLicenseId: number | null = null;

    @Input()
    allowAddingNotes = true;

    @HostBinding("class")
    readonly hostClass = "assessor-notes";

    readonly componentId = ++AssessorNotesFooterComponent.nextInstanceId;

    protected readonly noteForm: FormGroup;
    protected readonly editNoteForm: FormGroup;
    protected notes: AssessorNote[] = [];
    protected pageIndex = 1;
    protected readonly pageSize = 10;
    protected total = 0;
    private pageRequest: Subscription | null = null;
    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    protected filteredNotes: AssessorNote[] = [];
    protected authorOptions: string[] = [];
    protected areFiltersVisible = false;
    protected noteSearchText = "";
    protected selectedAuthorNames: string[] = [];
    protected isLoading = false;
    protected isExpanded = false;
    protected isFormVisible = false;
    protected isSubmitting = false;
    protected editingNoteId: number | null = null;
    protected isSavingEdit = false;
    protected hasSelection = false;
    protected canViewAssessorNotes = false;
    protected readonly deletingNoteIds = new Set<number>();
    protected readonly formatDateTimeDisplay = formatDateTimeDisplay;

    @HostBinding("style.display")
    protected get hostDisplay(): string | null {
        return this.canViewAssessorNotes ? null : "none";
    }

    private currentSelection: NavigationMenuSelection | null = null;
    private lastSelectionKey: string | null = null;
    private loadSequence = 0;
    private readonly destroy$ = new Subject<void>();
    private licenseAccessSubscription: Subscription | null = null;
    private readonly textEncoder = new TextEncoder();
    private pendingMutationRefresh = false;
    private editRequestSequence = 0;
    private editingLicenseId: number | null = null;
    private lastContextLicenseId: number | null = null;
    private lastAggregateByLicense = false;

    constructor(
        private readonly commentsService: QuestionResponseCommentsService,
        private readonly questionResponsesService: QuestionResponsesService,
        private readonly productLicensesService: ProductLicensesService,
        private readonly loginService: LoginService,
        private readonly notification: NzNotificationService,
        private readonly modal: NzModalService,
        private readonly navigationDispatcher: FormNavigationDispatcherService,
        private readonly fb: FormBuilder,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.noteForm = this.createNoteForm();
        this.editNoteForm = this.createNoteForm();

        this.questionResponsesService
            .watchMutationEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((mutation) => {
                if (!this.shouldRefreshForMutation(mutation)) {
                    return;
                }

                if (this.isLoading) {
                    this.pendingMutationRefresh = true;
                    return;
                }

                if (this.isAssessorNotesAlreadyCurrent(mutation)) {
                    return;
                }

                this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // The same footer instance can render another requirement; show its new
        // context collapsed without changing the existing note loading path.
        if (changes["sectionId"] || changes["sectionKey"] || changes["aggregateByLicense"] ||
            (changes["readOnlyUser"] && this.readOnlyUser)) {
            this.isExpanded = false;
        }
        if (changes["sectionId"] || changes["sectionKey"] || changes["readOnlyUser"] ||
            changes["aggregateByLicense"] || changes["productLicenseId"]) {
            this.clearEditState();
            this.updateSelection(this.sectionId, this.sectionKey);
        }
        if (changes["allowAddingNotes"] && !this.allowAddingNotes) {
            this.onCancelForm();
        }
    }

    ngOnDestroy(): void {
        this.pageRequest?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.destroy$.next();
        this.destroy$.complete();
        this.licenseAccessSubscription?.unsubscribe();
    }

    protected trackById(_index: number, item: AssessorNote): number {
        return item.id;
    }

    @HostListener("click")
    protected expandIfCollapsed(): void {
        if (!this.isExpanded) {
            this.isExpanded = true;
            this.markForCheck();
        }
    }

    protected toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
        this.markForCheck();
    }

    protected showFilters(): void {
        this.areFiltersVisible = true;
        this.markForCheck();
    }

    protected onNoteSearchChange(value: string): void {
        this.noteSearchText = typeof value === "string" ? value.slice(0, 256) : "";
        this.queuePageSearch();
        this.markForCheck();
    }

    protected onAuthorFilterChange(values: string[]): void {
        this.selectedAuthorNames = Array.isArray(values) ? values : [];
        this.pageIndex = 1;
        this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
        this.markForCheck();
    }

    protected isQaRole(role: string | null | undefined): boolean {
        if (!role) {
            return false;
        }

        return role.trim().toUpperCase() === "QA";
    }

    protected onAddNoteClicked(): void {
        if (this.readOnlyUser || !this.canViewAssessorNotes || !this.allowAddingNotes) {
            return;
        }

        if (!this.hasSelection && !this.aggregateByLicense) {
            this.notification.warning(
                "Select a requirement",
                "Choose a requirement before adding an assessor note."
            );
            return;
        }

        this.isFormVisible = true;
        this.isExpanded = true;
        this.isSubmitting = false;
        this.noteForm.reset({ body: "" });
        this.markForCheck();
    }

    protected onCancelForm(): void {
        this.isFormVisible = false;
        this.isSubmitting = false;
        this.noteForm.reset({ body: "" });
        this.markForCheck();
    }

    protected canEditNote(note: AssessorNote): boolean {
        const currentUserId = this.normalizeUserId(this.loginService.getCurrentUserId());
        return !this.readOnlyUser && this.canViewAssessorNotes &&
            note.userId !== null && note.userId === currentUserId;
    }

    protected canDeleteNote(note: AssessorNote): boolean {
        if (this.readOnlyUser || !this.canViewAssessorNotes) return false;
        const role = this.loginService.getCurrentUserRole()?.trim().toLowerCase();
        return this.loginService.hasAdminPrivileges() || role === "administrator" ||
            (note.userId !== null && note.userId === this.normalizeUserId(this.loginService.getCurrentUserId()));
    }

    protected wasNoteEdited(note: AssessorNote): boolean {
        return !!note.updatedAt && note.updatedAt !== note.createdAt;
    }

    protected onEditNote(note: AssessorNote): void {
        if (!this.canEditNote(note) || this.isLoading || this.isSavingEdit ||
            this.editingNoteId !== null || this.deletingNoteIds.has(note.id)) {
            return;
        }

        this.editingNoteId = note.id;
        this.editingLicenseId = this.getCurrentLicenseId();
        this.editNoteForm.reset({ body: note.body });
        this.markForCheck();
    }

    protected onCancelEdit(): void {
        if (this.isSavingEdit) {
            return;
        }

        this.clearEditState();
        this.markForCheck();
    }

    protected onSaveEdit(note: AssessorNote): void {
        if (this.editingNoteId !== note.id || !this.canEditNote(note) || this.isSavingEdit || this.isLoading) {
            return;
        }

        this.editNoteForm.markAllAsTouched();
        this.editNoteForm.updateValueAndValidity();
        if (this.editNoteForm.invalid) {
            this.markForCheck();
            return;
        }

        const body = this.normalizeSubmissionBody(String(this.editNoteForm.value.body ?? ""));
        if (!body) {
            this.notification.error("Edit note failed", "The note cannot be empty.");
            return;
        }
        if (body === note.body) {
            this.onCancelEdit();
            return;
        }

        this.modal.confirm({
            nzTitle: "Save changes to this assessment note?",
            nzContent: "The updated note will be visible to other users who can view assessment notes.",
            nzOkText: "Save",
            nzCancelText: "Cancel",
            nzCentered: true,
            nzOnOk: () => this.performEdit(note, body)
        });
    }

    private performEdit(note: AssessorNote, body: string): void {
        const selection = this.currentSelection;
        const licenseId = this.getCurrentLicenseId();
        if (this.editingNoteId !== note.id || !this.canEditNote(note) || this.isSavingEdit ||
            this.isLoading || !this.notes.some((item) => item.id === note.id && item.body === note.body) ||
            licenseId === null || licenseId !== this.editingLicenseId || (!selection && !this.aggregateByLicense)) {
            return;
        }

        const requestSequence = ++this.editRequestSequence;
        this.isSavingEdit = true;
        this.markForCheck();
        this.commentsService
            .updateComment(note.id, this.aggregateByLicense ? null : selection, { body }, licenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (requestSequence !== this.editRequestSequence) {
                        return;
                    }
                    this.notes = this.notes.map((item) => item.id === note.id ? this.normalizeNote(response) : item);
                    this.rebuildFilteredNotes();
                    this.isSavingEdit = false;
                    this.onCancelEdit();
                    this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
                    this.notification.success("Assessor note updated", "The assessment note has been saved.");
                },
                error: (error) => {
                    if (requestSequence !== this.editRequestSequence) {
                        return;
                    }
                    this.isSavingEdit = false;
                    const message = error?.error?.message || error?.message || "Unable to edit the assessment note.";
                    this.notification.error("Edit note failed", message);
                    this.markForCheck();
                }
            });
    }

    protected onSubmitNote(): void {
        if (this.isSubmitting || this.readOnlyUser || !this.canViewAssessorNotes || !this.allowAddingNotes) {
            return;
        }

        const selection = this.currentSelection;
        const licenseId = this.getCurrentLicenseId();
        const aggregateByLicense = this.aggregateByLicense;
        const reference = this.toQuestionReference(selection);
        if (!selection && !this.aggregateByLicense) {
            this.notification.error(
                "Add note failed",
                "A valid question context is not available."
            );
            return;
        }

        this.noteForm.markAllAsTouched();
        this.noteForm.updateValueAndValidity();

        if (this.noteForm.invalid) {
            this.markForCheck();
            return;
        }

        const payload: CreateQuestionResponseCommentPayload = {
            body: this.normalizeSubmissionBody(String(this.noteForm.value.body ?? ""))
        };

        if (!payload.body) {
            this.notification.error("Add note failed", "The note cannot be empty.");
            return;
        }

        if (licenseId === null) {
            return;
        }

        this.isSubmitting = true;
        this.markForCheck();

        this.commentsService
            .createComment(aggregateByLicense ? null : selection, payload, licenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (!this.isCurrentNoteContext(licenseId, reference, aggregateByLicense)) {
                        return;
                    }
                    this.pageIndex = 1;
                    this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
                    this.notification.success(
                        "Assessor note added",
                        "The assessor note has been saved."
                    );
                    this.isSubmitting = false;
                    this.isFormVisible = false;
                    this.noteForm.reset({ body: "" });
                    this.markForCheck();
                },
                error: (error) => {
                    if (!this.isCurrentNoteContext(licenseId, reference, aggregateByLicense)) {
                        return;
                    }
                    this.isSubmitting = false;
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to add assessor note.";
                    this.notification.error("Add note failed", message);
                    this.markForCheck();
                }
            });
    }

    protected onDeleteNote(note: AssessorNote): void {
        if (!this.canDeleteNote(note) || this.deletingNoteIds.has(note.id)) {
            return;
        }

        const licenseId = this.getCurrentLicenseId();
        const reference = this.toQuestionReference(this.currentSelection);
        const aggregateByLicense = this.aggregateByLicense;
        if (licenseId === null) {
            return;
        }

        this.modal.confirm({
            nzTitle: "Delete assessor note?",
            nzContent: "Are you sure you want to delete this note?",
            nzOkText: "Confirm",
            nzCancelText: "Cancel",
            nzOkDanger: true,
            nzCentered: true,
            nzOnOk: () => this.performDelete(note, licenseId, reference, aggregateByLicense)
        });
    }

    private performDelete(note: AssessorNote, licenseId: number, reference: string | null, aggregateByLicense: boolean): void {
        const selection = this.currentSelection;
        if (this.deletingNoteIds.has(note.id) ||
            !this.canDeleteNote(note) ||
            !this.notes.some((item) => item.id === note.id) ||
            !this.isCurrentNoteContext(licenseId, reference, aggregateByLicense) || (!selection && !aggregateByLicense)) {
            return;
        }

        this.deletingNoteIds.add(note.id);
        this.markForCheck();

        this.commentsService
            .deleteComment(note.id, aggregateByLicense ? null : selection, licenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    if (!this.isCurrentNoteContext(licenseId, reference, aggregateByLicense)) {
                        return;
                    }
                    this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
                    this.deletingNoteIds.delete(note.id);
                    this.notification.success(
                        "Assessor note removed",
                        "The assessor note has been deleted."
                    );
                    this.markForCheck();
                },
                error: (error) => {
                    if (!this.isCurrentNoteContext(licenseId, reference, aggregateByLicense)) {
                        return;
                    }
                    this.deletingNoteIds.delete(note.id);
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to delete the assessor note.";
                    this.notification.error("Delete failed", message);
                    this.markForCheck();
                }
            });
    }

    private updateSelection(sectionId: string, sectionKey: string): void {
        if (this.readOnlyUser) {
            this.licenseAccessSubscription?.unsubscribe();
            this.licenseAccessSubscription = null;
            this.notes = [];
            this.resetFilters();
            this.isLoading = false;
            this.hasSelection = false;
            this.currentSelection = null;
            this.lastSelectionKey = null;
            this.lastContextLicenseId = null;
            this.canViewAssessorNotes = false;
            ++this.loadSequence;
            this.pendingMutationRefresh = false;
            this.isFormVisible = false;
            this.isSubmitting = false;
            this.noteForm.reset({ body: "" });
            this.deletingNoteIds.clear();
            this.markForCheck();
            return;
        }

        const normalized = this.normalizeSelection(sectionId, sectionKey);
        const reference = normalized ? this.toQuestionReference(normalized) : null;
        const licenseId = this.getCurrentLicenseId();
        const contextChanged = reference !== this.lastSelectionKey ||
            licenseId !== this.lastContextLicenseId ||
            this.aggregateByLicense !== this.lastAggregateByLicense;
        if (contextChanged) {
            this.clearEditState();
            // Invalidate earlier loads before checking access to the new context.
            ++this.loadSequence;
            this.notes = [];
            this.resetFilters();
            this.isLoading = false;
            this.canViewAssessorNotes = false;
            this.pendingMutationRefresh = false;
        }
        this.lastContextLicenseId = licenseId;
        this.lastAggregateByLicense = this.aggregateByLicense;

        if (!contextChanged) {
            this.currentSelection = normalized;
            this.hasSelection = !!normalized;
            this.evaluateAccessPermissions(normalized);
            return;
        }

        this.lastSelectionKey = reference;
        this.currentSelection = normalized;
        this.hasSelection = !!normalized;
        this.isFormVisible = false;
        this.isSubmitting = false;
        this.noteForm.reset({ body: "" });
        this.deletingNoteIds.clear();

        if (!normalized && !this.aggregateByLicense) {
            this.notes = [];
            this.rebuildFilteredNotes();
            this.isLoading = false;
            this.canViewAssessorNotes = false;
            this.markForCheck();
            return;
        }

        this.evaluateAccessPermissions(normalized);
    }

    private loadNotes(selection: NavigationMenuSelection | null): void {
        this.pageRequest?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        const licenseId = this.getCurrentLicenseId();
        if (licenseId === null) {
            this.resetNotesState();
            this.markForCheck();
            return;
        }
        const requestId = ++this.loadSequence;

        this.isLoading = true;
        this.notes = [];
        this.rebuildFilteredNotes();
        this.markForCheck();

        this.pageRequest = this.commentsService
            .listPage(selection, licenseId, {
                page: this.pageIndex, pageSize: this.pageSize,
                search: this.noteSearchText, authors: this.selectedAuthorNames
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (items) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    const lastPage = Math.max(1, Math.ceil(items.total / this.pageSize));
                    if (this.pageIndex > lastPage) {
                        this.pageIndex = lastPage;
                        this.loadNotes(selection);
                        return;
                    }
                    this.total = items.total;
                    this.authorOptions = items.authors;
                    this.notes = items.items.map((item) => this.normalizeNote(item));
                    this.rebuildFilteredNotes();
                    if (this.editingNoteId !== null && !this.notes.some((note) => note.id === this.editingNoteId)) {
                        this.clearEditState();
                    }
                    this.isLoading = false;
                    if (this.pendingMutationRefresh) {
                        this.pendingMutationRefresh = false;
                        this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
                        return;
                    }
                    this.markForCheck();
                },
                error: (error) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    this.notes = [];
                    this.rebuildFilteredNotes();
                    this.clearEditState();
                    this.isLoading = false;
                    if (this.pendingMutationRefresh) {
                        this.pendingMutationRefresh = false;
                        this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
                        return;
                    }
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to load assessment notes.";
                    this.notification.error("Load failed", message);
                    this.markForCheck();
                }
            });
    }

    private evaluateAccessPermissions(selection: NavigationMenuSelection | null): void {
        const activeLicenseId = this.getCurrentLicenseId();
        this.licenseAccessSubscription?.unsubscribe();
        this.licenseAccessSubscription = null;

        if (this.loginService.hasAdminPrivileges()) {
            this.canViewAssessorNotes = activeLicenseId !== null;
            if (this.canViewAssessorNotes) {
                this.loadNotes(selection);
            } else {
                this.resetNotesState();
            }
            this.markForCheck();
            return;
        }

        if (activeLicenseId === null) {
            this.canViewAssessorNotes = false;
            this.resetNotesState();
            this.markForCheck();
            return;
        }

        this.canViewAssessorNotes = false;
        this.markForCheck();
        this.licenseAccessSubscription = this.productLicensesService
            .getLicenseById(activeLicenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (license) => {
                    if (activeLicenseId !== this.getCurrentLicenseId()) {
                        return;
                    }
                    this.canViewAssessorNotes = this.isUserAssignedToLicense(license);
                    if (this.canViewAssessorNotes) {
                        this.loadNotes(selection);
                    } else {
                        this.resetNotesState();
                    }
                    this.markForCheck();
                },
                error: () => {
                    if (activeLicenseId !== this.getCurrentLicenseId()) {
                        return;
                    }
                    this.canViewAssessorNotes = false;
                    this.resetNotesState();
                    this.markForCheck();
                }
            });
    }

    private resetNotesState(): void {
        ++this.loadSequence;
        this.clearEditState();
        this.notes = [];
        this.resetFilters();
        this.isLoading = false;
        this.pendingMutationRefresh = false;
        this.isFormVisible = false;
        this.isSubmitting = false;
        this.noteForm.reset({ body: "" });
        this.deletingNoteIds.clear();
    }

    private clearEditState(): void {
        ++this.editRequestSequence;
        this.editingNoteId = null;
        this.editingLicenseId = null;
        this.isSavingEdit = false;
        this.editNoteForm.reset({ body: "" });
    }

    private resetFilters(): void {
        this.pageIndex = 1;
        this.total = 0;
        this.authorOptions = [];
        this.pageRequest?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.areFiltersVisible = false;
        this.noteSearchText = "";
        this.selectedAuthorNames = [];
        this.rebuildFilteredNotes();
    }

    private rebuildFilteredNotes(): void {
        this.filteredNotes = this.notes;
    }

    private queuePageSearch(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        // Cancel the previous read immediately so a slow old search cannot replace the new one.
        this.pageRequest?.unsubscribe();
        ++this.loadSequence;
        this.searchTimer = setTimeout(() => {
            this.pageIndex = 1;
            this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
        }, 250);
    }

    protected onPageChange(page: number): void {
        if (!Number.isSafeInteger(page) || page < 1 || page === this.pageIndex ||
            this.isLoading || this.isSubmitting || this.editingNoteId !== null) return;
        this.pageIndex = page;
        this.loadNotes(this.aggregateByLicense ? null : this.currentSelection);
    }

    private isUserAssignedToLicense(license: ProductLicenseSummary | null): boolean {
        const userId = this.normalizeUserId(this.loginService.getCurrentUserId());
        if (!license || userId === null) {
            return false;
        }

        const assignmentIds = [
            license.assignedIsaUserId,
            license.assignedQsaUserId,
            license.assignedQaUserId,
            ...(license.editorIds ?? [])
        ]
            .map((id) => this.normalizeUserId(id))
            .filter((id): id is number => Number.isInteger(id));

        return assignmentIds.some((id) => id === userId);
    }

    private normalizeLicenseId(value: number | null): number | null {
        const numeric = Number(value);
        return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
    }

    private getCurrentLicenseId(): number | null {
        return this.normalizeLicenseId(this.aggregateByLicense
            ? this.productLicenseId
            : this.productLicenseId ?? this.questionResponsesService.getActiveProductLicenseId());
    }

    private isCurrentNoteContext(licenseId: number, reference: string | null, aggregateByLicense: boolean): boolean {
        return !this.readOnlyUser && this.canViewAssessorNotes &&
            licenseId === this.getCurrentLicenseId() &&
            aggregateByLicense === this.aggregateByLicense &&
            reference === this.toQuestionReference(this.currentSelection);
    }

    private normalizeUserId(value: unknown): number | null {
        const numeric = Number(value);
        return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    }

    private normalizeNote(dto: QuestionResponseCommentDto): AssessorNote {
        const id = Number(dto.id) || 0;
        const userId = this.normalizeUserId(dto.userId ?? dto.user_id);
        const body = this.sanitizeBody(dto.body ?? "");
        const userRole = this.normalizeRole(dto.userRole ?? dto.user_role ?? null);
        const createdAt = this.normalizeDate(dto.createdAt ?? dto.created_at) ?? new Date().toISOString();
        const updatedAt = this.normalizeDate(dto.updatedAt ?? dto.updated_at);
        const questionReference = dto.questionReference ?? dto.question_reference ?? null;
        const createdByDisplayName = this.sanitizeDisplayName(
            dto.createdByDisplayName ?? dto.created_by_display_name ?? null
        );

        return { id, userId, body, userRole, createdAt, updatedAt, questionReference, createdByDisplayName };
    }



    protected onRequirementReferenceClick(reference: string | null | undefined): void {
        const selection = this.parseSelectionFromReference(reference);
        if (!selection) {
            return;
        }

        this.navigationDispatcher.requestNavigation(selection);
    }

    protected getRequirementReferenceLabel(reference: string | null | undefined): string {
        const selection = this.parseSelectionFromReference(reference);
        if (!selection || (selection.section === "assessment_cover" && selection.target === "overview")) {
            return "";
        }

        return this.formatRequirementReferenceTarget(selection.target);
    }

    private formatRequirementReferenceTarget(target: string): string {
        const normalized = target.trim();
        if (!normalized) {
            return "";
        }

        if (normalized.toLowerCase() === "overview") {
            return "Assessment Overview";
        }

        return normalized.replace(/_/g, ".");
    }

    private parseSelectionFromReference(reference: string | null | undefined): NavigationMenuSelection | null {
        if (typeof reference !== "string") {
            return null;
        }

        const [section, target] = reference.split("|");
        if (!section || !target) {
            return null;
        }

        return this.normalizeSelection(section, target);
    }

    private shouldRefreshForMutation(mutation: QuestionResponseMutationResponse | null | undefined): boolean {
        if (!mutation || this.readOnlyUser || !this.canViewAssessorNotes) {
            return false;
        }

        const mutationLicenseId = this.normalizeLicenseId(mutation.productLicenseId ?? null);
        const activeLicenseId = this.getCurrentLicenseId();
        if (mutationLicenseId === null || activeLicenseId === null || mutationLicenseId !== activeLicenseId) {
            return false;
        }

        if (this.aggregateByLicense) {
            return true;
        }

        const mutationSelection = mutation.selection
            ? this.normalizeSelection(mutation.selection.section, mutation.selection.target)
            : null;
        const currentSelection = this.currentSelection
            ? this.normalizeSelection(this.currentSelection.section, this.currentSelection.target)
            : null;

        if (!mutationSelection || !currentSelection) {
            return false;
        }

        return (
            mutationSelection.section === currentSelection.section &&
            mutationSelection.target === currentSelection.target
        );
    }

    private isAssessorNotesAlreadyCurrent(mutation: QuestionResponseMutationResponse): boolean {
        const widget = mutation.widgets?.comments;
        if (!widget) {
            return false;
        }

        // Skip redundant reloads when both widget and local notes are already empty/current.
        if (this.notes.length === 0 && widget.total === 0) {
            return true;
        }

        const firstNote = this.notes[0];
        if (!firstNote) {
            return false;
        }

        const latestMutationTimestamp = this.normalizeDate(widget.latestUpdatedAt ?? widget.latestCreatedAt);
        const firstNoteTimestamp = this.normalizeDate(firstNote.updatedAt ?? firstNote.createdAt);
        if (!latestMutationTimestamp || !firstNoteTimestamp) {
            return false;
        }

        return widget.total === this.notes.length && latestMutationTimestamp === firstNoteTimestamp;
    }
    private normalizeSelection(sectionId: string | null | undefined, sectionKey: string | null | undefined): NavigationMenuSelection | null {
        const section = typeof sectionId === "string" ? sectionId.trim() : "";
        const target = typeof sectionKey === "string" ? sectionKey.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }

    private toQuestionReference(selection: NavigationMenuSelection | null): string | null {
        if (!selection) {
            return null;
        }

        return `${selection.section}|${selection.target}`;
    }

    private sanitizeBody(value: string): string {
        return value
            .replace(/\r\n?/g, "\n")
            .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
            .trim();
    }

    private sanitizeDisplayName(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }

        const sanitized = String(value)
            .replace(/[\u0000-\u001F\u007F<>]+/g, " ")
            .replace(/\s{2,}/g, " ")
            .trim();

        return sanitized || null;
    }

    private normalizeRole(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }

        const normalized = String(value).trim();
        return normalized || null;
    }

    private normalizeDate(value: string | Date | null | undefined): string | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value.toISOString();
        }

        const trimmed = typeof value === "string" ? value.trim() : "";
        if (!trimmed) {
            return null;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    private normalizeSubmissionBody(value: string): string {
        return this.sanitizeBody(value);
    }

    private createNoteForm(): FormGroup {
        return this.fb.group({
            body: ["", [
                Validators.required,
                Validators.maxLength(65535),
                this.createWhitespaceValidator(),
                this.createByteLengthValidator(65535)
            ]]
        });
    }

    private createWhitespaceValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const value = typeof control.value === "string" ? control.value.trim() : "";
            if (!value) {
                return { whitespace: true };
            }
            return null;
        };
    }

    private createByteLengthValidator(maxBytes: number): ValidatorFn {
        return (control: AbstractControl) => {
            const value = typeof control.value === "string" ? control.value : "";
            if (!value) {
                return null;
            }

            const normalized = this.normalizeSubmissionBody(value);
            if (!normalized) {
                return { whitespace: true };
            }

            if (this.calculateByteLength(normalized) > maxBytes) {
                return { byteLength: true };
            }

            return null;
        };
    }

    private markForCheck(): void {
        this.cdr.markForCheck();
    }

    private calculateByteLength(value: string): number {
        return this.textEncoder.encode(value).length;
    }
}
